<template>
    <div class="alert alert-warning mb-6 text-sm" v-if="utmItems.length">
        <p>&bull; Before show or export report, click and select the date range.</p>
        <p>&bull; <b>Show Report</b>: click the "Show Report" button next to the date filter.</p>
        <p>&bull; <b>Export Report</b>: click the "Export Report" button next to the "Show Report" button to download the CSV file.</p>
    </div>

    <div class="mt-4 py-4 border-t" v-if="!utmItems.length">
        <h4>There is no UTMs data. Please try again later.</h4>
    </div>

    <div class="flex flex-wrap mb-2" v-if="utmItems.length">
        <div class="xs:w-full pr-3 xs:pr-0">
            <label for="From">From</label>
            <input class="form-input-inline-w-none" type="date" id="From" v-model="form.date_from" />
        </div>
        <div class="xs:w-full pr-3 xs:pr-0">
            <label for="To">To</label>
            <input class="form-input-inline-w-none" type="date" id="To" v-model="form.date_to" />
        </div>
        <div class="xs:w-full pr-2 xs:pr-0">
            <a @click="showReport()" class="btn btn-yellow btn-sm inline-block xs:mx-auto w-full" :class="{'btn-disabled': !form.date_from || !form.date_to}">
                <span class="fa fa-eye"></span>
                Show Report
            </a>
        </div>
        <div class="xs:w-full">
            <a @click="downloadReport()" class="btn btn-blue btn-sm inline-block xs:mx-auto w-full" :class="{'btn-disabled': !dataLoaded || !form.date_from || !form.date_to}">
                <span class="fa fa-download"></span>
                Export Report
            </a>
        </div>
    </div>

    <div class="mt-4 py-4 border-t" v-if="utmItems.length">
        <div class="relative border-t-2 border-blue-600 mt-4">
            <div class="table-header border-b">
                <div class="flex flex-wrap w-full">
                    <div class="font-bold" v-if="dataLoaded">
                        List from {{ form.date_from }} to {{ form.date_to }}, {{ utmItems.length }} UTMs entries
                    </div>
                    <div class="font-bold" v-if="!dataLoaded">
                        Last {{ utmItems.length }} UTMs entries
                    </div>
                </div>
            </div>

            <div class="table-responsive">
                <table class="table table-striped table-hover border border-t-0 admin-table">
                    <thead>
                        <tr>
                            <th class="w-32">Date</th>
                            <th>User ID</th>
                            <th>Email</th>
                            <th>Banker ID</th>
                            <th>UTM Source</th>
                            <th>UTM Medium</th>
                            <th>UTM Campaign</th>
                            <th>UTM Content</th>
                        </tr>
                    </thead>
                    <tbody>
                    <template v-for="item in utmItems" :key="item.id">
                        <tr>
                            <td nowrap>{{ item.created_at }}</td>
                            <td>{{ item.user_id }}</td>
                            <td>{{ item.email }}</td>
                            <td>{{ item.banker_id }}</td>
                            <td>{{ item.source }}</td>
                            <td>{{ item.medium }}</td>
                            <td>{{ item.campaign }}</td>
                            <td>{{ item.content }}</td>
                        </tr>
                    </template>
                    </tbody>
                    <tfoot>
                        <tr>
                            <th class="w-32">Date</th>
                            <th>User ID</th>
                            <th>Email</th>
                            <th>Banker ID</th>
                            <th>UTM Source</th>
                            <th>UTM Medium</th>
                            <th>UTM Campaign</th>
                            <th>UTM Content</th>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    </div>
    <flash-message ref="flash"></flash-message>
</template>

<script setup>
import { ref, watch } from 'vue';

const props = defineProps({
    latest: {
        type: Array,
        required: true
    },
});

const form = ref({
    date_from: '',
    date_to: new Date().toISOString().split('T')[0],
});

const dataLoaded = ref(false);
const utmItems = ref(props.latest);
const flash = ref(null);

watch(() => form.value.date_from, (newValue, oldValue) => {
    if (newValue > form.value.date_to) {
        flash.value.error('Invalid date value.');
        form.value.date_from = oldValue;
    }
    dataLoaded.value = false;
});

watch(() => form.value.date_to, (newValue, oldValue) => {
    if (newValue < form.value.date_from) {
        flash.value.error('Invalid date value.');
        form.value.date_to = oldValue;
    }
    dataLoaded.value = false;
});

function showReport() {
    dataLoaded.value = false;
    const route = window.route('admin.promotions-report.show-report');
    axios.post(route, form.value)
        .then((response) => {
            if (response.data.items && response.data.items.length) {
                utmItems.value = response.data.items;
                dataLoaded.value = true;
            } else {
                flash.value.error('Error retrieving the UTMs data!');
            }
        }).catch(error => {
            flash.value.error('Error retrieving the UTMs data!');
        });
}

function downloadReport() {
    if (utmItems.value.length) {
        let csv = 'data:text/csv;charset=utf-8,';
        csv += '"Date","User ID","Email","Banker ID","UTM Source","UTM Medium","UTM Campaign","UTM Content"\r\n';
        const fields = ['created_at', 'user_id', 'email', 'banker_id', 'source', 'medium', 'campaign', 'content'];
        utmItems.value.forEach((row) => {
            csv += fields.map((field) => `"${row[field]}"`).join() + `\r\n`;
        });

        let fileName = `Promotions Report (${form.value.date_from}_${form.value.date_to}).csv`;
        const link = document.createElement('a');
        link.setAttribute('href', encodeURI(csv));
        link.setAttribute('download', fileName);
        document.body.appendChild(link);

        link.click();

        document.body.removeChild(link);
    } else {
        flash.value.error('Error retrieving the UTMs data!');
    }
}
</script>
